/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Product } from '../../storage';
import { useSelector } from 'react-redux';
import { selectCurrentCart } from '../../../cart/storage/cart.slice';
import { Button } from '../../../../components/Button';
import Counter from '../../../../components/Counter';
import { useMediaQuery, useTheme } from '@mui/material';
import TestIds from '../../../../testIds';
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage';

interface QuickOrderButtonProps {
  item: Product;
  onChangeQuantity?: (value: number) => void;
  handleAddToCart?: () => void;
  handleShowOptions?: () => void;
  handleEnquireOptions?: () => void;
  isAlternativeProduct?: boolean;
}
const QuickOrderButton = ({
  item,
  onChangeQuantity,
  handleAddToCart,
  handleShowOptions,
  handleEnquireOptions,
  isAlternativeProduct,
}: QuickOrderButtonProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const cart = useSelector(selectCurrentCart);
  const inCart = cart?.items.find((x) => x.productId === item.productId);
  const enableShowOptions =
    item.hasVariants || (isAlternativeProduct && (item.colour || item.size));
  const enquireOnlyButton = item?.stockAvailability === 'Enquire';

  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const isTfo = currentAccount?.hasTfoAccess;

  const styles: React.CSSProperties = {
    marginTop: isSmall ? 3 : 'auto',
    transition: 'all 0.5s ease',
    whiteSpace: 'nowrap',
  };

  if (item.category === 'Services') {
    return null;
  }

  if (!isTfo && enquireOnlyButton) {
    return (
      <Button
        key='product-enquiry-button'
        variant='contained'
        onClick={handleEnquireOptions}
        sx={styles}
        data-testid={TestIds.feat.product.card.enquireButton}
      >
        Enquire
      </Button>
    );
  }

  if (enableShowOptions) {
    return (
      <Button
        key='product-show-options-button'
        variant='contained'
        onClick={handleShowOptions}
        sx={styles}
        data-testid={TestIds.feat.product.card.showOptionsButton}
      >
        Show options
      </Button>
    );
  }

  if (inCart) {
    return (
      <Counter
        sx={styles}
        value={inCart.quantity}
        onChange={(value) => {
          if (value > 0 && onChangeQuantity) {
            onChangeQuantity(value);
          }
        }}
      />
    );
  }

  return (
    <Button
      key='product-cart-button'
      variant='contained'
      onClick={handleAddToCart}
      sx={styles}
      data-testid={TestIds.feat.product.card.orderButton}
    >
      Add to cart
    </Button>
  );
};

export default QuickOrderButton;
